// eslint-disable-next-line max-classes-per-file
import { devWarning } from '@tanium/coreui-utils';
import { DateFormatterInput, isDateWithTimezoneProps, isValidDate } from './dateFormatters';
import { FormatterMap, getFormatterMaps } from './formatterMaps';
import { DurationProps, isDurationProps } from './relativeTimeFormatters';

class FormattingError extends Error {
  constructor(message: string, public loudMessage: string = message.toUpperCase()) {
    super(message);
  }
}
class FormatValueError extends FormattingError {
  private static formatValueForError(value: unknown) {
    if (typeof value === 'object' && !(value instanceof Date)) {
      return JSON.stringify(value);
    }

    return String(value);
  }

  constructor(preamble: string, value: unknown) {
    const formattedValue = FormatValueError.formatValueForError(value);

    const message = `${preamble} ${formattedValue}`;
    const loudMessage = `${preamble.toUpperCase()} ${formattedValue}`;

    super(message, loudMessage);
  }
}
class FormatValueTypeError extends FormatValueError {
  constructor(expectedType: string, value: unknown) {
    const a = /^[aeiou]/i.test(expectedType) ? 'an' : 'a';
    const preamble = `Can't apply ${a} ${expectedType} format to`;
    super(preamble, value);
  }
}

const assertNumber: TypeAssertionFunction<number> = (value) => {
  if (typeof value !== 'number') {
    throw new FormatValueTypeError('numeric', value);
  }
};

const assertDate: TypeAssertionFunction<Date> = (value) => {
  if (!isValidDate(value)) {
    throw new FormatValueTypeError('date', value);
  }
};
const assertStringArray: TypeAssertionFunction<string[]> = (value) => {
  if (!Array.isArray(value)) {
    throw new FormatValueTypeError('array', value);
  }
};

const assertDateProps: TypeAssertionFunction<DateFormatterInput> = (value) => {
  if (!isDateWithTimezoneProps(value) && !isValidDate(value)) {
    throw new FormatValueTypeError('date', value);
  }
};

const assertDuration: TypeAssertionFunction<DurationProps> = (value) => {
  if (!isDurationProps(value)) {
    throw new FormatValueTypeError('duration', value);
  }
};

type TypeAssertionFunction<T> = (value: unknown) => asserts value is T;

// This function is "unsafe" because it throws an error if there's a problem with the supplied value and/or format.
const unsafeFormatter = (value: unknown, format: string | undefined, lng: string): string => {
  const {
    numericFormatterMap,
    stringArrayFormatterMap,
    dateFormatterMap,
    relativeDateFormatters,
    durationFormatterMap,
  } = getFormatterMaps(lng);

  if (format) {
    const checkFormatterMap = <T>(assert: TypeAssertionFunction<T>, map: FormatterMap<T>) => {
      const formatter = map[format];

      if (formatter) {
        assert(value);
        return formatter(value);
      }

      return undefined;
    };

    const formatted =
      checkFormatterMap(assertNumber, numericFormatterMap) ??
      checkFormatterMap(assertStringArray, stringArrayFormatterMap) ??
      checkFormatterMap(assertDateProps, dateFormatterMap) ??
      checkFormatterMap(assertDate, relativeDateFormatters) ??
      checkFormatterMap(assertDuration, durationFormatterMap);

    if (typeof formatted === 'string') return formatted;

    throw new FormattingError(`The format '${format}' does not exist`);
  }

  if (isValidDate(value))
    throw new FormatValueError('Interpolated dates must have a format specified:', value);
  if (typeof value === 'number')
    throw new FormatValueError('Interpolated numbers must have a format specified:', value);
  if (typeof value !== 'string')
    throw new FormatValueError(
      'Only strings can be interpolated when there is no format specified. Received:',
      value,
    );

  return value;
};

// This wraps the "unsafe" formatter to fail a little more gracefully.
export const formatter = (
  value: unknown,
  format: string | undefined,
  lng: string | undefined,
): string => {
  if (lng === undefined)
    throw new Error(
      'Current locale must be specified as the language for interpolation formatting',
    );

  try {
    return unsafeFormatter(value, format, lng);
  } catch (error: unknown) {
    if (process.env.NODE_ENV === 'production') return '';

    devWarning(false, error instanceof Error ? error.message : error);

    const formattingError: FormattingError = (() => {
      if (error instanceof FormattingError) return error;

      return format
        ? new FormatValueError(`An error occurred while applying the "${format}" format to`, value)
        : new FormatValueError('An error occurred while formatting', value);
    })();

    return `🛑🚫⛔️ BROKEN TRANSLATION: ${formattingError.loudMessage} ⛔️🚫🛑`;
  }
};
